import { ChangeEventHandler, FocusEventHandler } from 'react'
import {
  FaCheck,
  FaCompass,
  FaLock,
  FaMapMarkedAlt,
  FaPen,
  FaUsers
} from 'react-icons/fa'

import Button from 'components/button'
import CollaborationPicker from 'components/collaborationPicker'

import { TRAVEL_INTERESTS } from 'travelInterests'

import * as S from '../../style'

type PainelDescriptionProps = {
  handleChange: ChangeEventHandler<HTMLTextAreaElement>
  handleBlur: FocusEventHandler<HTMLTextAreaElement>
  values: any
  setFieldValue: (field: string, value: any) => void
  className: string
  prevStep: () => void
}

const PainelDescription = ({
  handleChange,
  handleBlur,
  values,
  setFieldValue,
  className,
  prevStep
}: PainelDescriptionProps) => {
  const selectedInterests = Array.isArray(values.interests)
    ? values.interests
    : []
  const isPrivate = values.made === false || String(values.made) === 'false'

  const toggleInterest = (value: string) => {
    const next = selectedInterests.includes(value)
      ? selectedInterests.filter((item: string) => item !== value)
      : [...selectedInterests, value]

    setFieldValue('interests', next)
  }

  return (
    <S.PainelCreateItinerary className={className}>
      <S.FinalStepWorkspace>
        <S.FinalStepHeader>
          <S.BasicInfoEyebrow>
            <FaCompass />
            Etapa 3 de 3
          </S.BasicInfoEyebrow>
          <h2>Dê contexto ao seu roteiro</h2>
          <p>
            Finalize com os interesses da viagem, convide quem participou e
            deixe um resumo para ajudar outros viajantes — ou apenas você, se o
            roteiro for privado.
          </p>
        </S.FinalStepHeader>

        <S.FinalStepGrid>
          <S.FinalStepColumn>
            <S.FinalCard>
              <S.FinalCardHeader>
                <FaCompass />
                <div>
                  <strong>Como você descreveria essa viagem?</strong>
                  <span>
                    Escolha quantas opções fizerem sentido. Elas ajudam a
                    organizar e descobrir roteiros parecidos.
                  </span>
                </div>
              </S.FinalCardHeader>

              <S.InterestGridModern>
                {TRAVEL_INTERESTS.map((interest) => {
                  const active = selectedInterests.includes(interest.value)

                  return (
                    <S.InterestChip
                      key={interest.value}
                      type="button"
                      $active={active}
                      onClick={() => toggleInterest(interest.value)}
                    >
                      {active && <FaCheck />}
                      {interest.label}
                    </S.InterestChip>
                  )
                })}
              </S.InterestGridModern>
            </S.FinalCard>

            <S.FinalCard>
              <S.FinalCardHeader>
                <FaPen />
                <div>
                  <strong>Resumo da viagem</strong>
                  <span>
                    Conte os principais aprendizados, destaques, surpresas e
                    dicas. Esse texto complementa o dia a dia do roteiro.
                  </span>
                </div>
              </S.FinalCardHeader>

              <S.SummaryField>
                <textarea
                  maxLength={5000}
                  id="summary"
                  name="summary"
                  value={values.summary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="O que mais vale saber sobre essa viagem? Quais foram os destaques, pontos fortes, dificuldades e dicas que você daria para quem pretende fazer algo parecido?"
                />
                <small>{String(values.summary || '').length}/5000</small>
              </S.SummaryField>
            </S.FinalCard>
          </S.FinalStepColumn>

          <S.FinalStepColumn>
            <S.FinalCard>
              <S.FinalCardHeader>
                <FaUsers />
                <div>
                  <strong>Colaboração</strong>
                  <span>
                    Se outras pessoas participaram da viagem, convide-as para
                    também fazer parte do roteiro.
                  </span>
                </div>
              </S.FinalCardHeader>

              <CollaborationPicker
                selectedIds={values.collaboratorIds || []}
                onChange={(ids) => setFieldValue('collaboratorIds', ids)}
              />
            </S.FinalCard>

            <S.FinalCard>
              <S.FinalCardHeader>
                {isPrivate ? <FaLock /> : <FaMapMarkedAlt />}
                <div>
                  <strong>Antes de salvar</strong>
                  <span>
                    Confira como este roteiro ficará visível na RedSterna.
                  </span>
                </div>
              </S.FinalCardHeader>

              <S.PublishPreview $private={isPrivate}>
                {isPrivate ? <FaLock /> : <FaMapMarkedAlt />}
                <div>
                  <strong>
                    {isPrivate
                      ? 'Este roteiro ficará privado'
                      : 'Este roteiro ficará público no mapa'}
                  </strong>
                  <span>
                    {isPrivate
                      ? 'Somente você poderá acessá-lo enquanto estiver como Planejado.'
                      : 'Outros viajantes poderão encontrar o roteiro, abrir os dias e usá-lo como inspiração.'}
                  </span>
                </div>
              </S.PublishPreview>
            </S.FinalCard>
          </S.FinalStepColumn>
        </S.FinalStepGrid>
      </S.FinalStepWorkspace>

      <S.FinalActions>
        <Button type="button" secondary onClick={prevStep}>
          <p>Voltar</p>
        </Button>
        <Button type="submit">
          <p>Salvar roteiro</p>
        </Button>
      </S.FinalActions>
    </S.PainelCreateItinerary>
  )
}

export default PainelDescription
